import { Link } from "react-router";
import { Alerta, Button, Card, DataList, MetricCard, Row, Spinner, Stack } from "@/components";
import type { ImporteCashFlow, MesCashFlow, RubroMesCashFlow, SinFechaCashFlow, SinPresupuestoCashFlow } from "@/lib/api";
import { comprometido, estimado, hayPesos, manoDeObra, materiales, textoFee, textoMes, textoSinFecha } from "@/lib/cashFlow";
import { formatMoneda } from "@/lib/format";
import { formatCantidad } from "@/lib/presupuesto";
import { useCashFlowTab } from "./useCashFlowTab";

const usd = (v: string) => formatMoneda(v, "USD");
const enlace = "font-medium text-primario underline-offset-2 hover:underline";

type FilaMes = { tipo: "mes"; m: MesCashFlow } | { tipo: "total"; i: ImporteCashFlow };
type FilaSinFecha = { tipo: "item"; s: SinFechaCashFlow; n: number } | { tipo: "total"; i: ImporteCashFlow };

/** Las columnas de importes, iguales para el mes y para el rubro. */
function columnasImporte<T>(de: (f: T) => ImporteCashFlow) {
  return [
    { clave: "cm", titulo: "Comprometido materiales", render: (f: T) => usd(de(f).comprometido_materiales) },
    { clave: "cmo", titulo: "Comprometido mano de obra", render: (f: T) => usd(de(f).comprometido_mano_obra) },
    { clave: "em", titulo: "Estimado materiales", render: (f: T) => usd(de(f).estimado_materiales) },
    { clave: "emo", titulo: "Estimado mano de obra", render: (f: T) => usd(de(f).estimado_mano_obra) },
    { clave: "fee", titulo: "Fee", render: (f: T) => usd(de(f).fee) },
    {
      clave: "total",
      titulo: "Total",
      render: (f: T) => (
        <span>
          <strong>{usd(de(f).total)}</strong>
          {hayPesos(de(f)) && <span className="block text-chico text-texto-suave">{`${formatMoneda(de(f).ars, "ARS")} en pesos`}</span>}
        </span>
      ),
    },
  ];
}

/** Plata → Cash flow (sólo admin): cuándo sale la plata de la obra, mes a mes (docs/modulo-cash-flow.md). */
export function CashFlowTab() {
  const t = useCashFlowTab();
  const cf = t.cashFlow;

  const filasMeses: FilaMes[] = cf && cf.meses.length ? [...cf.meses.map((m): FilaMes => ({ tipo: "mes", m })), { tipo: "total", i: cf.total }] : [];
  const columnasMes = [
    { clave: "mes", titulo: "Mes", render: (f: FilaMes) => (f.tipo === "total" ? <strong>Total</strong> : <span className="first-letter:uppercase">{textoMes(f.m.mes)}</span>) },
    ...columnasImporte<FilaMes>((f) => (f.tipo === "total" ? f.i : f.m)),
    { clave: "acumulado", titulo: "Acumulado", render: (f: FilaMes) => (f.tipo === "total" ? "—" : usd(f.m.acumulado)) },
  ];
  const accionesMes = (f: FilaMes) =>
    f.tipo === "total" ? null : (
      <Button variante="secundario" tamanio="sm" aria-label={`Ver ${textoMes(f.m.mes)} por rubro`} onClick={() => t.alternarMes(f.m)}>
        {t.mesAbierto?.mes === f.m.mes ? "Cerrar" : "Por rubro"}
      </Button>
    );

  const columnasRubro = [
    { clave: "rubro", titulo: "Rubro", render: (r: RubroMesCashFlow) => (r.rubro_id === null ? r.rubro : <Link to={`/presupuesto/rubros/${r.rubro_id}`} className={enlace}>{r.rubro}</Link>) },
    ...columnasImporte<RubroMesCashFlow>((r) => r),
  ];

  const filasSinFecha: FilaSinFecha[] = cf && cf.sin_fecha.length ? [...cf.sin_fecha.map((s, n): FilaSinFecha => ({ tipo: "item", s, n })), { tipo: "total", i: cf.total_sin_fecha }] : [];
  const columnasSinFecha = [
    {
      clave: "que",
      titulo: "Qué",
      render: (f: FilaSinFecha) => {
        if (f.tipo === "total") return <strong>Total</strong>;
        const texto = textoSinFecha(f.s);
        return (
          <Stack espacio={1}>
            {f.s.cotizacion_id !== null ? <Link to={`/cotizaciones/${f.s.cotizacion_id}`} className={enlace}>{texto}</Link> : texto}
            <span className="text-chico text-texto-suave">{f.s.origen === "comprometido" ? "Comprometido" : "Estimado"}</span>
          </Stack>
        );
      },
    },
    { clave: "motivo", titulo: "Por qué no tiene fecha", render: (f: FilaSinFecha) => (f.tipo === "total" ? "—" : f.s.motivo) },
    { clave: "materiales", titulo: "Materiales", render: (f: FilaSinFecha) => usd(materiales(f.tipo === "total" ? f.i : f.s)) },
    { clave: "mo", titulo: "Mano de obra", render: (f: FilaSinFecha) => usd(manoDeObra(f.tipo === "total" ? f.i : f.s)) },
    { clave: "fee", titulo: "Fee", render: (f: FilaSinFecha) => usd((f.tipo === "total" ? f.i : f.s).fee) },
    { clave: "total", titulo: "Total", render: (f: FilaSinFecha) => <strong>{usd((f.tipo === "total" ? f.i : f.s).total)}</strong> },
  ];

  const columnasSinPresupuesto = [
    { clave: "rubro", titulo: "Rubro", render: (i: SinPresupuestoCashFlow) => <Link to={`/presupuesto/rubros/${i.rubro_id}`} className={enlace}>{i.rubro}</Link> },
    { clave: "item", titulo: "Ítem", render: (i: SinPresupuestoCashFlow) => <span className="whitespace-pre-line">{i.descripcion}</span> },
    { clave: "cantidad", titulo: "Cantidad", render: (i: SinPresupuestoCashFlow) => formatCantidad(i) },
  ];

  return (
    <Stack espacio={6}>
      <h1 className="text-pantalla font-light tracking-[-0.03em]">Cash flow</h1>

      {t.cargando && (
        <div className="grid place-items-center py-10 text-primario">
          <Spinner />
        </div>
      )}
      {t.error && <Alerta texto={t.error} />}

      {cf && (
        <>
          <p className="text-chico text-texto-suave">
            Cuándo sale la plata de la obra, mes a mes, en dólares. <strong>Comprometido</strong>: lo que falta pagar de los presupuestos
            aceptados, según su forma de pago (lo ya pagado de sus facturas no aparece). <strong>Estimado</strong>: lo presupuestado de los ítems que ningún presupuesto aceptado cubre (los materiales al empezar el
            ítem y la mano de obra repartida mientras dura). <strong>Fee</strong>: {textoFee(cf.fee_porcentaje)}.
          </p>
          {!cf.inicio && (
            <Alerta
              tono="alerta"
              texto="Falta la fecha de inicio de obra: sin ella, lo que depende del cronograma va sin fecha."
              accion={<Link to="/cronograma" className="font-medium underline underline-offset-2">Fijala en Cronograma</Link>}
            />
          )}
          {cf.avisos.map((a) => (
            <Alerta
              key={a.texto}
              tono="alerta"
              texto={a.texto}
              accion={<Link to={`/cotizaciones/${a.cotizacion_id}`} className="font-medium underline underline-offset-2">Ver el presupuesto</Link>}
            />
          ))}

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <MetricCard titulo="Comprometido" valor={usd(comprometido(cf.total))} detalle={`Materiales ${usd(cf.total.comprometido_materiales)} · Mano de obra ${usd(cf.total.comprometido_mano_obra)}`} />
            <MetricCard titulo="Estimado" valor={usd(estimado(cf.total))} detalle={`Materiales ${usd(cf.total.estimado_materiales)} · Mano de obra ${usd(cf.total.estimado_mano_obra)}`} />
            <MetricCard titulo="Fee" valor={usd(cf.total.fee)} />
            <MetricCard titulo="Total con fecha" valor={usd(cf.total.total)} detalle={cf.sin_fecha.length ? `Sin fecha: ${usd(cf.total_sin_fecha.total)}` : undefined} />
          </div>

          <DataList
            etiqueta="Cash flow por mes"
            columnas={columnasMes}
            filas={filasMeses}
            claveFila={(f) => (f.tipo === "total" ? "total" : f.m.mes)}
            acciones={accionesMes}
            vacio="Todavía no hay pagos ni estimados con fecha: cargá la forma de pago de los presupuestos aceptados y el cronograma."
          />

          {t.mesAbierto && (
            <Card>
              <Stack espacio={3}>
                <Row justificar="entre" envolver espacio={2}>
                  <h2 className="text-titulo font-light tracking-tight first-letter:uppercase">{`${textoMes(t.mesAbierto.mes)} por rubro`}</h2>
                  <Button variante="fantasma" tamanio="sm" onClick={t.cerrarMes}>
                    Cerrar
                  </Button>
                </Row>
                <DataList etiqueta={`${textoMes(t.mesAbierto.mes)} por rubro`} columnas={columnasRubro} filas={t.mesAbierto.rubros} claveFila={(r) => r.rubro_id ?? "sin-rubro"} vacio="Sin movimiento este mes." />
              </Stack>
            </Card>
          )}

          {filasSinFecha.length > 0 && (
            <Stack espacio={3}>
              <h2 className="text-titulo font-light tracking-tight">Sin fecha</h2>
              <p className="text-chico text-texto-suave">No entran en la tabla hasta que tengan fecha: completá la forma de pago o el cronograma.</p>
              <DataList etiqueta="Sin fecha" columnas={columnasSinFecha} filas={filasSinFecha} claveFila={(f) => (f.tipo === "total" ? "total" : f.n)} />
            </Stack>
          )}

          {cf.sin_presupuesto.length > 0 && (
            <Stack espacio={3}>
              <h2 className="text-titulo font-light tracking-tight">Sin presupuesto asignado</h2>
              <p className="text-chico text-texto-suave">
                Ítems sin precio que ningún presupuesto aceptado cubre: no se estiman. Cargales el precio en Obra → Presupuesto.
              </p>
              <DataList etiqueta="Ítems sin presupuesto asignado" columnas={columnasSinPresupuesto} filas={cf.sin_presupuesto} claveFila={(i) => i.item_id} />
            </Stack>
          )}
        </>
      )}
    </Stack>
  );
}
